'use client';

import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import api from '@/lib/api';
import {
  LoginCredentials,
  UserData,
  LoginResponse,
  AuthError,
} from '../types/auth';
import { parseApiError } from '../lib/errorUtils';

// Role constants - keep in sync with backend
const USER_ROLES = {
  PARENT: 'PARENT',
} as const;

const performApiLogin = async (
  credentials: LoginCredentials,
): Promise<LoginResponse> => {
  try {
    const response = await api.post<LoginResponse>('/auth/login', credentials);
    if (!response.data || !response.data.accessToken || !response.data.user) {
      throw new Error('Login failed: Invalid response from server.');
    }
    return response.data;
  } catch (error: any) {
    throw parseApiError(error);
  }
};

interface UseLoginHandlerReturn {
  loginUser: UseMutationResult<
    LoginResponse,
    Error,
    LoginCredentials
  >['mutate'];
  isLoggingIn: boolean;
  loginError: AuthError | Error | null;
  resetLoginAttempt: () => void;
}

export function useLoginHandler(): UseLoginHandlerReturn {
  const { login: storeAuthData, logout: clearAuthData } = useAuth();
  const { addToast } = useToast();
  const router = useRouter();

  const mutation = useMutation<LoginResponse, Error, LoginCredentials>({
    mutationFn: performApiLogin,
    onSuccess: (data) => {
      if (data.user.role === USER_ROLES.PARENT) {
        storeAuthData(data.user, data.accessToken);
        addToast('Login successful! Redirecting...', 'success');

        // Use setTimeout to ensure token is stored before navigation
        setTimeout(() => {
          router.replace('/');
        }, 100);
      } else {
        // clearAuthData();
        throw new AuthError('Incorrect credentials.', true, false);
      }
    },
    onError: (error: Error) => {
      if (error instanceof AuthError && error.isNetworkError) {
        return;
      }
      addToast(
        error.message || 'Login failed. Please check your credentials.',
        'error',
      );
    },
  });

  return {
    loginUser: mutation.mutate,
    isLoggingIn: mutation.isPending,
    loginError: mutation.error,
    resetLoginAttempt: mutation.reset,
  };
}
